import React, { useState } from 'react';
import {
  Box, Button, Typography, Stack, InputLabel, Alert, useTheme
} from '@mui/material';
import Papa from 'papaparse';
import { useItemValidator } from '../../hooks/useItemValidator';
import { normalizeItemName } from '../../utils/normalizeItemName';
import { fetchWithAuth } from "../../utils/api";

const AdminMigrateData = () => {
  const theme = useTheme();
  const [file, setFile] = useState(null);
  const [filename, setFilename] = useState('');
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [validatedTrades, setValidatedTrades] = useState([]);
  const [isValidated, setIsValidated] = useState(false);
  const [validationSnackbar, setValidationSnackbar] = useState(false);
  const { validate } = useItemValidator();

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    const isCsv =
      selected &&
      (selected.type === 'text/csv' ||
        selected.name.toLowerCase().endsWith('.csv'));

    if (isCsv) {
      setFile(selected);
      setFilename(selected.name);
      setErrors([]);
      setSuccess(false);
      setIsValidated(false);
      setValidatedTrades([]);
    } else {
      setErrors(['Only .csv files are allowed.']);
      setFile(null);
      setFilename('');
      setIsValidated(false);
      setValidatedTrades([]);
    }
  };

  const normalizeItems = (itemString) => {
    if (!itemString) return itemString;
    return itemString
      .split('+')
      .map(entry => {
        const match = entry.match(/^(.*?)(\s*\([^)]+\))$/);
        if (!match) return normalizeItemName(entry.trim());
        const [, rawName, value] = match;
        const normalized = normalizeItemName(rawName.trim());
        return `${normalized}${value}`;
      })
      .join(' + ');
  };

  const validateTradeRow = (row, index) => {
    const rowErrors = [];
    if (!row.username || !row.trade_date || !row.items_sent || !row.items_received) {
      rowErrors.push(`Row ${index + 1}: Missing required fields.`);
    }
    if (row.trade_date && !/^\d{4}-\d{2}-\d{2}$/.test(row.trade_date)) {
      rowErrors.push(`Row ${index + 1}: Invalid date format.`);
    }

    const sentNormalized = normalizeItems(row.items_sent);
    const recvNormalized = normalizeItems(row.items_received);
    const sentResult = validate(sentNormalized);
    const recvResult = validate(recvNormalized);

    if (sentResult.errors.length) {
      rowErrors.push(`Row ${index + 1} Items Sent: ${sentResult.errors.join('; ')}`);
    }
    if (recvResult.errors.length) {
      rowErrors.push(`Row ${index + 1} Items Received: ${recvResult.errors.join('; ')}`);
    }

    return { rowErrors, sentNormalized, recvNormalized };
  };

  const handleValidate = () => {
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data;
        let allErrors = [];
        const validTrades = [];

        rows.forEach((row, i) => {
          const { rowErrors, sentNormalized, recvNormalized } = validateTradeRow(row, i);
          if (rowErrors.length) {
            allErrors.push(...rowErrors);
          } else {
            validTrades.push({
              username: row.username.trim(),
              tradeDate: row.trade_date.trim(),
              itemsSent: sentNormalized,
              itemsReceived: recvNormalized,
              notes: row.notes?.trim() || ''
            });
          }
        });

        if (allErrors.length) {
          setErrors(allErrors);
          setValidatedTrades([]);
          setIsValidated(false);
          setSuccess(false);
          setValidationSnackbar(false);
        } else {
          setErrors([]);
          setValidatedTrades(validTrades);
          setIsValidated(true);
          setSuccess(false);
          setValidationSnackbar(true);
        }
      },
      error: (err) => {
        setErrors(['Error parsing CSV file.']);
        console.error(err);
        setIsValidated(false);
        setValidationSnackbar(false);
      },
    });
  };

  const handleSubmit = async () => {
    if (!isValidated || validatedTrades.length === 0) return;

    const chunkSize = 400;
    const chunks = [];
    for (let i = 0; i < validatedTrades.length; i += chunkSize) {
      chunks.push(validatedTrades.slice(i, i + chunkSize));
    }

    console.log(
      `Split validated trades into ${chunks.length} request(s). Trades per request in order: ${chunks.map(c => c.length).join(", ")}`
    );

    try {
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const sizeKB = (new TextEncoder().encode(JSON.stringify(chunk)).length / 1024).toFixed(2);
        console.log(`Request ${i + 1} (${chunk.length} trades, ~${sizeKB} KB) starting...`);

        await fetchWithAuth("/report/bulk", {
          method: "POST",
          body: chunk,
        });

        console.log(`Request ${i + 1} (${chunk.length} trades): passed success`);
      }

      // If all succeeded
      setSuccess(true);
      setSuccessMessage(
        `Successfully submitted ${validatedTrades.length} trades in ${chunks.length} request(s). Trades per request: ${chunks
          .map(c => c.length)
          .join(", ")}.`
      );
      setErrors([]);
      setIsValidated(false);
      setValidatedTrades([]);
      setFile(null);
      setFilename('');
      setValidationSnackbar(false);

    } catch (err) {
      console.error(err);
      setErrors([err.message || "Failed to submit trades."]);
      setSuccess(false);
      setSuccessMessage("");
    }
  };

  return (
    <Box sx={{ px: 2, py: 4 }}>
      <Typography
        variant="h5"
        sx={{
          color: theme.palette.accent.main,
          fontWeight: "bold",
          textAlign: "center",
          mb: 3,
        }}
      >
        admin: migrate trades via csv
      </Typography>

      <Box
        sx={{
          maxWidth: 750,
          mx: "auto",
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRadius: 2,
          p: 4,
          boxShadow: 3,
        }}
      >
        <Stack spacing={3}>
          <Stack>
            <InputLabel htmlFor="csv-upload" sx={{ color: theme.palette.text.primary }}>
              Upload CSV File
            </InputLabel>
            <input
              id="csv-upload"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{
                color: theme.palette.text.primary,
              }}
            />
            {filename && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected File: <strong>{filename}</strong>
              </Typography>
            )}
          </Stack>

          {errors.length > 0 && (
            <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>
              {errors.join('\n')}
            </Alert>
          )}

          {success && (
            <Alert severity="success">
              {successMessage}
            </Alert>
          )}

          {validationSnackbar && (
            <Alert
              severity="success"
              onClose={() => setValidationSnackbar(false)}
            >
              Trades successfully validated.
            </Alert>
          )}

          <Button
            variant="outlined"
            color="secondary"
            onClick={handleValidate}
            disabled={!file}
          >
            Validate Trades
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!isValidated}
          >
            Submit Trades
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default AdminMigrateData;
